import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useAttrs,
  useChainedCommands,
  useCurrentSelection,
  usePositioner,
  useUpdateReason,
} from "@remirror/react";
import { getMarkRange } from "remirror";
import { createMarkPositioner, getPositioner } from "remirror/extensions";

import { Button, Icon, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import { useFocusState } from "./useFocusState";

const getLinkHrefFromSelection = (
  attrs: ReturnType<typeof useAttrs>,
  selection: ReturnType<typeof useCurrentSelection>,
) => {
  const hrefFromAttrs = attrs.link()?.href;
  if (hrefFromAttrs) {
    return String(hrefFromAttrs);
  }

  for (const $pos of [selection.$from, selection.$to]) {
    const linkMark = $pos
      .marks()
      .find(
        (mark: { type: { name: string }; attrs: { href?: string } }) =>
          mark.type.name === "link",
      );
    if (linkMark?.attrs?.href) {
      return String(linkMark.attrs.href);
    }
  }

  return "";
};

const preventEditorFocusSteal = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.target instanceof HTMLInputElement) {
    e.target.focus();
    return;
  }
  const input = e.currentTarget.querySelector<HTMLInputElement>("input");
  input?.focus();
};

function useFloatingLinkState() {
  const chain = useChainedCommands();
  const selection = useCurrentSelection();
  const { empty } = selection;

  const [isEditing, setIsEditing] = useState(false);

  const attrs = useAttrs();
  const url = useMemo(
    () => getLinkHrefFromSelection(attrs, selection),
    [attrs, selection],
  );
  const [href, setHref] = useState(url);

  useEffect(() => {
    if (!isEditing) {
      setHref(url);
    }
  }, [url, isEditing]);

  // A positioner which only shows for links.
  const linkPositioner = useMemo(
    () => createMarkPositioner({ type: "link" }),
    [],
  );
  const onRemove = useCallback(() => chain.removeLink().focus().run(), [chain]);
  const updateReason = useUpdateReason();

  // Only close on document changes — selection updates fire when the editor
  // blurs to the URL input and would immediately cancel edit mode.
  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }

    if (updateReason.doc) {
      setIsEditing(false);
      setHref("");
    }
  }, [isEditing, updateReason.doc]);

  const submitHref = useCallback(() => {
    setIsEditing(false);

    if (href === "") {
      chain.removeLink();
    } else {
      const markRange = getMarkRange(selection.$from, "link");
      chain.updateLink({ href, auto: false }, markRange);
    }

    chain.focus(selection?.to).run();
  }, [chain, href, selection]);

  const cancelHref = useCallback(() => {
    setIsEditing(false);
    chain.focus().run();
  }, [chain]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        cancelHref();
      }
      if (event.key === "Enter") {
        submitHref();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [isEditing, cancelHref, submitHref]);

  const clickEdit = useCallback(() => {
    if (empty) {
      chain.selectLink();
    }

    setHref(url);
    setIsEditing(true);
  }, [chain, url, empty]);

  return useMemo(
    () => ({
      url,
      href,
      setHref,
      linkPositioner,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref,
      selection: { empty },
    }),
    [
      url,
      href,
      linkPositioner,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref,
      empty,
    ],
  );
}

const FloatingToolbar = ({
  positioner = "selection",
  children,
  keepVisible = false,
}: {
  positioner?: string;
  children: React.ReactNode;
  keepVisible?: boolean;
}) => {
  const { ref, x, y, width, height, active } = usePositioner(
    () => getPositioner(positioner),
    [positioner],
  );
  const isFocused = useFocusState();

  const inlineStyle: CSSProperties = useMemo(
    () => ({
      position: "absolute",
      pointerEvents: "none",
      left: x,
      top: y,
      width,
      height,
    }),
    [x, y, width, height],
  );

  if (!isFocused && !keepVisible) {
    return null;
  }

  return (
    <Popup
      mountNode={document.getElementById("semantic-modal-mount-node")}
      trigger={<div ref={ref} style={inlineStyle} />}
      open={active}
      content={<div>{children}</div>}
      basic
      style={{ padding: 0, border: "none" }}
    />
  );
};

export const FloatingLinkToolbar = () => {
  const {
    url,
    isEditing,
    linkPositioner,
    clickEdit,
    onRemove,
    submitHref,
    href,
    setHref,
    cancelHref,
  } = useFloatingLinkState();

  const handleClickEdit = useCallback(() => {
    clickEdit();
  }, [clickEdit]);

  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    requestAnimationFrame(() => {
      urlInputRef.current?.focus({ preventScroll: true });
    });
  }, [isEditing]);

  return (
    <FloatingToolbar
      positioner={url ? linkPositioner : undefined}
      keepVisible={isEditing}
    >
      {isEditing ?
        <div
          style={{ display: "flex", alignItems: "center", gap: 4 }}
          onMouseDown={preventEditorFocusSteal}
        >
          <Input
            ref={urlInputRef}
            type="text"
            value={href}
            placeholder="Enter link..."
            onChange={(e) => {
              setHref(e.target.value);
            }}
            style={{ width: 450 }}
            action
          />
          <Button basic icon onClick={submitHref} type="submit">
            <Icon name="check" />
          </Button>
          <Button basic icon onClick={cancelHref}>
            <Icon name="close" />
          </Button>
        </div>
      : url ?
        <div
          style={{ position: "relative" }}
          onMouseDown={preventEditorFocusSteal}
        >
          <div
            style={{
              position: "absolute",
              padding: "4px 8px",
              bottom: 36,
              left: 0,
              background: "var(--colorNeutralBackground4, #eaeaea)",
              borderRadius: 4,
              alignItems: "center",
              fontSize: "0.75rem",
              lineHeight: "0.85rem",
              wordBreak: "break-all",
              maxWidth: 350,
              width: "max-content",
              gap: 3,
            }}
          >
            <Icon
              name="linkify"
              size="small"
              style={{ top: -1, position: "relative" }}
            />
            {url}
          </div>
          <Button.Group>
            <Button basic onClick={handleClickEdit} icon>
              <Icon name="pencil" />
            </Button>
            <Button basic onClick={onRemove} icon>
              <Icon name="unlinkify" />
            </Button>
          </Button.Group>
        </div>
      : <Button.Group onMouseDown={preventEditorFocusSteal}>
          <Button basic onClick={handleClickEdit} icon>
            <Icon name="linkify" />
          </Button>
        </Button.Group>
      }
    </FloatingToolbar>
  );
};
